/**
 * New page (1.I-1 deliverables 10 and 21): `docs.create`, then the page. A refusal raises a
 * fixed notice. "New page inside" also homes the page under its parent with a primary
 * `contains` link.
 */
import { docsCreate, linksCreate } from '@codex/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { api } from '../auth/client.js';
import { useNotify } from './notices.js';

export const CREATE_FAILED = "The page couldn't be created. Try again.";
export const PLACE_FAILED =
  "The new page was created but couldn't be placed. It's at the top of the tree.";

export interface CreateDeps {
  readonly create: (typeKey: string) => Promise<{ readonly id: string }>;
  readonly open: (docId: string) => void;
  readonly notify: (text: string) => void;
}

export async function createPage(deps: CreateDeps, typeKey = 'note'): Promise<void> {
  let id: string;
  try {
    id = (await deps.create(typeKey)).id;
  } catch {
    deps.notify(CREATE_FAILED);
    return;
  }
  deps.open(id);
}

/** The child type of "New page inside": quest under an arc, script under a quest, else note. */
export function childTypeFor(parentTypeKey: string): string {
  if (parentTypeKey === 'arc') return 'quest';
  if (parentTypeKey === 'quest') return 'script';
  return 'note';
}

export interface CreateChildDeps extends CreateDeps {
  /** `links.create` on the parent: a primary `contains` link to the new page. */
  readonly place: (parentId: string, targetId: string) => Promise<unknown>;
}

export async function createChildPage(
  deps: CreateChildDeps,
  parent: { readonly id: string; readonly typeKey: string },
): Promise<void> {
  let id: string;
  try {
    id = (await deps.create(childTypeFor(parent.typeKey))).id;
  } catch {
    deps.notify(CREATE_FAILED);
    return;
  }
  try {
    await deps.place(parent.id, id);
  } catch {
    deps.notify(PLACE_FAILED);
  }
  deps.open(id);
}

/** The "New page" action of the sidebar and of home. */
export function useCreatePage(): (typeKey?: string) => Promise<void> {
  const navigate = useNavigate();
  const notify = useNotify();
  return useCallback(
    (typeKey) =>
      createPage(
        {
          create: (key) => api(docsCreate, { body: { typeKey: key } }),
          open: (id) => void navigate({ to: '/doc/$id', params: { id } }),
          notify,
        },
        typeKey,
      ),
    [navigate, notify],
  );
}

/** "New page inside" on a tree row. */
export function useCreateChildPage(): (parent: { id: string; typeKey: string }) => Promise<void> {
  const navigate = useNavigate();
  const notify = useNotify();
  const client = useQueryClient();
  return useCallback(
    async (parent) => {
      await createChildPage(
        {
          create: (key) => api(docsCreate, { body: { typeKey: key } }),
          place: (parentId, targetId) =>
            api(linksCreate, {
              params: { id: parentId },
              body: { linkTypeKey: 'contains', targetId, isPrimary: true },
            }),
          open: (id) => {
            void client.invalidateQueries({ queryKey: ['tree'] });
            void navigate({ to: '/doc/$id', params: { id } });
          },
          notify,
        },
        parent,
      );
    },
    [navigate, notify, client],
  );
}
